"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";

type FirstGreenBrick = {
  index: number;
  datetime: string;
  open: number;
  close: number;
  brick_size: number;
  direction: string;
  is_reversal: boolean;
  volume: number;
};

type ReversalPattern = {
  is_reversal_pattern: boolean;
  last_red_low: number;
  first_green_high: number;
  reversal_strength: number;
  bricks_in_downtrend: number;
};

type ResultRow = {
  symbol: string;
  current_price: number;
  date: string;
  brick_size: number;
  brick_size_method: string;
  entry_price?: number;
  stop_loss?: number;
  entry_distance_bricks?: number;
  first_green_brick: FirstGreenBrick;
  reversal_pattern: ReversalPattern;
  total_bricks: number;
};

type ApiResponse = {
  ok: boolean;
  scanner: string;
  timestamp: string;
  method: string;
  brick_size_value: number;
  total_scanned: number;
  total_matched: number;
  count: number;
  results: ResultRow[];
};

type VolumeSnapshot = {
  latest: number;
  average: number;
  surge: number;
};

export default function RenkoFirstGreenPage() {
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [volumeMap, setVolumeMap] = useState<Record<string, VolumeSnapshot>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<Partial<ApiResponse>>({});
  
  // Filters
  const [minReversalStrength, setMinReversalStrength] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("edge_score");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const apiSortBy = sortBy === "volume_pulse" || sortBy === "edge_score" ? "reversal_strength" : sortBy;
        const params = new URLSearchParams({
          min_reversal_strength: String(minReversalStrength),
          sort_by: apiSortBy,
        });
        const res = await fetch(`/api/outputs/renko_first_green_candle.json?${params}`);
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data: ApiResponse = await res.json();
        setRows(data.results || []);
        setMetadata({
          scanner: data.scanner,
          timestamp: data.timestamp,
          method: data.method,
          brick_size_value: data.brick_size_value,
          total_scanned: data.total_scanned,
          total_matched: data.total_matched,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [minReversalStrength, sortBy]);

  useEffect(() => {
    const loadVolumes = async () => {
      if (!rows.length) {
        setVolumeMap({});
        return;
      }

      const pairs = await Promise.all(
        rows.slice(0, 24).map(async (row) => {
          try {
            const response = await fetch("/api/prices/ohlcv", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ symbol: row.symbol, limit: 12 }),
            });

            if (!response.ok) {
              return [row.symbol, { latest: 0, average: 0, surge: 0 }] as const;
            }

            const json = (await response.json()) as { results?: Array<{ volume?: number }> };
            const series = Array.isArray(json.results) ? json.results : [];
            const volumes = series.map((entry) => (typeof entry.volume === "number" ? entry.volume : 0)).filter((value) => value > 0);
            const latest = volumes[volumes.length - 1] ?? 0;
            const prior = volumes.slice(0, -1);
            const average =
              prior.length > 0 ? prior.reduce((total, value) => total + value, 0) / prior.length : latest;
            const surge = average > 0 ? latest / average : 0;

            return [row.symbol, { latest, average, surge }] as const;
          } catch {
            return [row.symbol, { latest: 0, average: 0, surge: 0 }] as const;
          }
        }),
      );

      setVolumeMap(Object.fromEntries(pairs));
    };

    void loadVolumes();
  }, [rows]);

  // Filter and display rows
  const displayRows = React.useMemo(() => {
    const getVolumePulse = (row: ResultRow) => {
      const snapshot = volumeMap[row.symbol];
      return (snapshot?.latest || 0) * Math.max(snapshot?.surge || 0, 1);
    };

    const getEdgeScore = (row: ResultRow) => {
      const snapshot = volumeMap[row.symbol];
      const volumeSurge = snapshot?.surge || 0;
      return row.reversal_pattern.reversal_strength * 28 + volumeSurge * 18 + row.reversal_pattern.bricks_in_downtrend * 3;
    };

    return [...rows]
      .sort((left, right) => {
        if (sortBy === "price") return right.current_price - left.current_price;
        if (sortBy === "brick_size") return right.brick_size - left.brick_size;
        if (sortBy === "date") return new Date(right.date).getTime() - new Date(left.date).getTime();
        if (sortBy === "volume_pulse") return getVolumePulse(right) - getVolumePulse(left);
        if (sortBy === "edge_score") return getEdgeScore(right) - getEdgeScore(left);
        return right.reversal_pattern.reversal_strength - left.reversal_pattern.reversal_strength;
      })
      .slice(0, 20);
  }, [rows, sortBy, volumeMap]);

  const maxVolume = React.useMemo(
    () => Math.max(1, ...displayRows.map((row) => volumeMap[row.symbol]?.latest || 0)),
    [displayRows, volumeMap],
  );

  const summary = React.useMemo(() => {
    const getEdgeScore = (row: ResultRow) => {
      const snapshot = volumeMap[row.symbol];
      const volumeSurge = snapshot?.surge || 0;
      return row.reversal_pattern.reversal_strength * 28 + volumeSurge * 18 + row.reversal_pattern.bricks_in_downtrend * 3;
    };

    const topVolume = [...displayRows].sort((left, right) => (volumeMap[right.symbol]?.latest || 0) - (volumeMap[left.symbol]?.latest || 0))[0];
    const strongestEdge = [...displayRows].sort((left, right) => getEdgeScore(right) - getEdgeScore(left))[0];
    const averageReversal =
      displayRows.length > 0
        ? displayRows.reduce((total, row) => total + row.reversal_pattern.reversal_strength, 0) / displayRows.length
        : 0;

    return {
      topVolume,
      strongestEdge,
      averageReversal,
    };
  }, [displayRows, volumeMap]);

  // Helper: Get reversal strength badge
  const getReversalBadge = (strength: number) => {
    let bgColor = "rgba(148, 163, 184, 0.2)";
    let textColor = "#94a3b8";
    let label = "EARLY";
    
    if (strength >= 3) {
      bgColor = "rgba(16, 185, 129, 0.25)";
      textColor = "var(--color-success)";
      label = "STRONG";
    } else if (strength >= 1.5) {
      bgColor = "rgba(34, 197, 94, 0.2)";
      textColor = "var(--color-success)";
      label = "BUILDING";
    } else if (strength >= 0.5) {
      bgColor = "rgba(251, 191, 36, 0.2)";
      textColor = "#fbbf24";
      label = "WATCH";
    }
    
    return (
      <span
        style={{
          display: "inline-block",
          padding: "0.25rem 0.6rem",
          borderRadius: "4px",
          fontSize: "0.75rem",
          fontWeight: 600,
          background: bgColor,
          color: textColor,
        }}
      >
        {label} {strength.toFixed(2)}%
      </span>
    );
  };

  // Helper: Format numbers to remove .00
  const formatNumber = (num: number, digits = 2): string => {
    if (Number.isInteger(num)) {
      return num.toString();
    }
    return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
  };

  // Helper: Calculate risk/reward (Reward to Risk ratio)
  const calculateRiskReward = (row: ResultRow): string => {
    if (!row.reversal_pattern.is_reversal_pattern) return "—";
    
    const entry = row.first_green_brick.close;
    const stopLoss = row.reversal_pattern.last_red_low;
    const target = entry + (entry - stopLoss) * 2; // 2:1 Reward:Risk target
    const risk = entry - stopLoss;
    const reward = target - entry;
    
    if (risk <= 0) return "—";
    const rr = reward / risk;
    return formatNumber(rr);
  };

  const formatVolume = (num: number): string => {
    if (!num) return "—";
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toFixed(0);
  };

  const setupWeight = (row: ResultRow): number => {
    const snapshot = volumeMap[row.symbol];
    const volumeSurge = snapshot?.surge || 0;
    return row.reversal_pattern.reversal_strength * 28 + volumeSurge * 18 + row.reversal_pattern.bricks_in_downtrend * 3;
  };

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Renko First Green Candle</h1>
          <p className="page-subtitle">
            Detect early reversal signals with first green Renko brick formations
          </p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          {/* Info Banner */}
          <div style={{
            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "12px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
          }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.15rem", color: "var(--color-text)", fontWeight: 700 }}>
              📊 Renko First Green Candle Scanner
            </h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
              {/* What is Renko */}
              <div style={{ background: "rgba(30, 41, 59, 0.4)", padding: "1rem", borderRadius: "8px", border: "1px solid rgba(148, 163, 184, 0.2)" }}>
                <div style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>🧱</div>
                <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.95rem", color: "var(--color-text)", fontWeight: 600 }}>What is Renko?</h4>
                <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                  Renko charts show <strong>fixed price movements</strong> (bricks) without time. Each brick = one full price move.
                </p>
              </div>
              
              {/* Reversal Signal */}
              <div style={{ background: "rgba(30, 41, 59, 0.4)", padding: "1rem", borderRadius: "8px", border: "1px solid rgba(148, 163, 184, 0.2)" }}>
                <div style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>🔄</div>
                <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.95rem", color: "var(--color-text)", fontWeight: 600 }}>Reversal Signal</h4>
                <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                  After red bricks (downtrend), the <strong>first green brick</strong> signals a potential reversal with low risk.
                </p>
              </div>
              
              {/* Trading Setup */}
              <div style={{ background: "rgba(30, 41, 59, 0.4)", padding: "1rem", borderRadius: "8px", border: "1px solid rgba(148, 163, 184, 0.2)" }}>
                <div style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>🎯</div>
                <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.95rem", color: "var(--color-text)", fontWeight: 600 }}>Trading Setup</h4>
                <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                  <strong>Entry:</strong> Green brick close • <strong>Stop:</strong> Last red low - 0.5 brick • <strong>Target:</strong> 2:1 R:R
                </p>
              </div>

              <div style={{ background: "rgba(30, 41, 59, 0.4)", padding: "1rem", borderRadius: "8px", border: "1px solid rgba(148, 163, 184, 0.2)" }}>
                <div style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>📶</div>
                <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.95rem", color: "var(--color-text)", fontWeight: 600 }}>Magnitude Matters</h4>
                <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                  First-green reversals are now weighted by recent volume pulse so quiet moves do not read like real breakouts.
                </p>
              </div>
            </div>
            
            {metadata.method && (
              <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", background: "rgba(16, 185, 129, 0.05)", padding: "0.75rem", borderRadius: "6px", border: "1px solid rgba(16, 185, 129, 0.15)" }}>
                <strong>Scanner Config:</strong> {metadata.method} method • {metadata.brick_size_value}x multiplier • Scanned {metadata.total_scanned} shares • Found {metadata.total_matched} formations
              </div>
            )}
          </div>

          {/* Filters */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            flexWrap: "wrap",
            padding: "1rem",
            background: "rgba(30, 41, 59, 0.4)",
            borderRadius: "8px",
            marginBottom: "1.5rem",
            border: "1px solid rgba(148, 163, 184, 0.2)"
          }}>
            {/* Min Reversal Strength */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", fontWeight: 500 }}>
                Min Reversal:
              </label>
              <select 
                value={minReversalStrength} 
                onChange={(e) => setMinReversalStrength(Number(e.target.value))}
                style={{ 
                  padding: "0.35rem 0.6rem",
                  borderRadius: "6px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "rgba(30, 41, 59, 0.5)",
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.85rem"
                }}
              >
                <option value={0}>All</option>
                <option value={0.5}>≥ 0.5%</option>
                <option value={1}>≥ 1.0%</option>
                <option value={1.5}>≥ 1.5%</option>
                <option value={2}>≥ 2.0%</option>
                <option value={3}>≥ 3.0%</option>
              </select>
            </div>

            {/* Sort By */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", fontWeight: 500 }}>
                Sort:
              </label>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                style={{ 
                  padding: "0.35rem 0.6rem",
                  borderRadius: "6px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "rgba(30, 41, 59, 0.5)",
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.85rem"
                }}
              >
                <option value="reversal_strength">Reversal Strength</option>
                <option value="edge_score">Setup Weight</option>
                <option value="volume_pulse">Volume Pulse</option>
                <option value="price">Price</option>
                <option value="brick_size">Brick Size</option>
                <option value="date">Date</option>
              </select>
            </div>

            {/* Results Count */}
            <div style={{ 
              marginLeft: "auto", 
              color: "var(--color-text-muted)", 
              fontSize: "0.85rem",
              padding: "0.35rem 0.75rem",
              background: "rgba(16, 185, 129, 0.1)",
              borderRadius: "6px",
              border: "1px solid rgba(16, 185, 129, 0.2)"
            }}>
              Showing {displayRows.length} formations
            </div>
          </div>

          {!loading && !error && displayRows.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "0.85rem",
                marginBottom: "1.25rem",
              }}
            >
              <div style={{ padding: "1rem", borderRadius: "10px", border: "1px solid rgba(148, 163, 184, 0.18)", background: "rgba(15, 23, 42, 0.55)" }}>
                <div style={{ fontSize: "0.78rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-muted)", marginBottom: "0.35rem" }}>Top Volume Breakout</div>
                <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-text)" }}>{summary.topVolume?.symbol || "—"}</div>
                <div className="tabular-nums" style={{ marginTop: "0.35rem", color: "var(--color-success)", fontWeight: 700 }}>
                  {formatVolume(volumeMap[summary.topVolume?.symbol || ""]?.latest || 0)}
                </div>
              </div>
              <div style={{ padding: "1rem", borderRadius: "10px", border: "1px solid rgba(148, 163, 184, 0.18)", background: "rgba(15, 23, 42, 0.55)" }}>
                <div style={{ fontSize: "0.78rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-muted)", marginBottom: "0.35rem" }}>Strongest Setup Weight</div>
                <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-text)" }}>{summary.strongestEdge?.symbol || "—"}</div>
                <div className="tabular-nums" style={{ marginTop: "0.35rem", color: "#93c5fd", fontWeight: 700 }}>
                  {summary.strongestEdge ? `${formatNumber(setupWeight(summary.strongestEdge), 0)} pts` : "—"}
                </div>
              </div>
              <div style={{ padding: "1rem", borderRadius: "10px", border: "1px solid rgba(148, 163, 184, 0.18)", background: "rgba(15, 23, 42, 0.55)" }}>
                <div style={{ fontSize: "0.78rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-muted)", marginBottom: "0.35rem" }}>Average Reversal</div>
                <div className="tabular-nums" style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-text)" }}>
                  {formatNumber(summary.averageReversal)}%
                </div>
                <div style={{ marginTop: "0.35rem", color: "var(--color-text-muted)", fontSize: "0.82rem" }}>
                  Current scan quality across the visible reversal stack.
                </div>
              </div>
            </div>
          )}

          {loading && <div style={{ color: "var(--color-text-muted)" }}>Loading...</div>}
          {error && <div style={{ color: "#fca5a5" }}>{error}</div>}

          {!loading && !error && displayRows.length === 0 && (
            <div style={{ 
              textAlign: "center", 
              padding: "3rem", 
              color: "var(--color-text-muted)",
              background: "rgba(30, 41, 59, 0.3)",
              borderRadius: "8px",
              border: "1px solid rgba(148, 163, 184, 0.1)"
            }}>
              <p style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>No formations found</p>
              <p style={{ fontSize: "0.9rem", opacity: 0.8 }}>Try adjusting your filters or check back later</p>
            </div>
          )}

          {!loading && !error && displayRows.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Price</th>
                    <th>Reversal</th>
                    <th>Volume Pulse</th>
                    <th>Setup Weight</th>
                    <th>Entry</th>
                    <th>Stop Loss</th>
                    <th>Risk</th>
                    <th title="Reward to Risk - how much profit for each unit of risk">R:R (Reward:Risk)</th>
                    <th>Downtrend</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row, idx) => {
                    const entry = row.entry_price ?? row.first_green_brick.close;
                    const stopLoss = row.stop_loss ?? (row.reversal_pattern.is_reversal_pattern 
                      ? row.reversal_pattern.last_red_low 
                      : null);
                    const risk = stopLoss ? entry - stopLoss : null;
                    const riskReward = calculateRiskReward(row);
                    const snapshot = volumeMap[row.symbol];
                    const pulseWidth = `${Math.max(12, ((snapshot?.latest || 0) / maxVolume) * 100)}%`;
                    const pulseTone = (snapshot?.surge || 0) >= 1.5 ? "var(--color-success)" : (snapshot?.surge || 0) >= 1 ? "#38bdf8" : "#94a3b8";
                    
                    return (
                      <tr key={`${row.symbol}-${idx}`}>
                        {/* Symbol */}
                        <td style={{ fontWeight: 600 }}>
                          <Link 
                            href={`/stock/${row.symbol}`}
                            style={{ color: "var(--color-text)", textDecoration: "none" }}
                          >
                            {row.symbol}
                          </Link>
                        </td>
                        
                        {/* Current Price */}
                        <td style={{ fontWeight: 500 }}>{formatNumber(row.current_price)}</td>
                        
                        {/* Reversal Strength */}
                        <td>
                          {row.reversal_pattern.is_reversal_pattern 
                            ? getReversalBadge(row.reversal_pattern.reversal_strength)
                            : <span style={{ color: "var(--color-text-muted)" }}>—</span>
                          }
                        </td>

                        <td style={{ minWidth: "180px" }}>
                          <div style={{ display: "grid", gap: "0.3rem" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                              <span className="tabular-nums" style={{ color: "var(--color-text)", fontWeight: 600 }}>{formatVolume(snapshot?.latest || 0)}</span>
                              <span className="tabular-nums" style={{ color: pulseTone, fontWeight: 700 }}>
                                {snapshot?.surge ? `${formatNumber(snapshot.surge)}x` : "—"}
                              </span>
                            </div>
                            <div style={{ height: "8px", borderRadius: "999px", background: "rgba(30, 41, 59, 0.92)", overflow: "hidden" }}>
                              <div style={{ width: pulseWidth, height: "100%", borderRadius: "999px", background: `linear-gradient(90deg, rgba(59, 130, 246, 0.9), ${pulseTone})` }} />
                            </div>
                          </div>
                        </td>

                        <td className="tabular-nums" style={{ color: "#93c5fd", fontWeight: 700 }}>
                          {formatNumber(setupWeight(row), 0)}
                        </td>
                        
                        {/* Entry */}
                        <td className="tabular-nums" style={{ color: "var(--color-success)", fontWeight: 600 }}>
                          {formatNumber(entry)}
                        </td>
                        
                        {/* Stop Loss */}
                        <td className="tabular-nums" style={{ color: "var(--color-danger)", fontWeight: 600 }}>
                          {stopLoss ? formatNumber(stopLoss) : "—"}
                        </td>
                        
                        {/* Risk */}
                        <td className="tabular-nums" style={{ color: "var(--color-text-muted)" }}>
                          {risk ? formatNumber(risk) : "—"}
                        </td>
                        
                        {/* Risk:Reward */}
                        <td style={{ 
                          color: riskReward !== "—" && Number(riskReward) >= 2 ? "var(--color-success)" : "var(--color-text)",
                          fontWeight: riskReward !== "—" && Number(riskReward) >= 2 ? 600 : 400
                        }} className="tabular-nums">
                          {riskReward !== "—" ? `${riskReward}:1` : "—"}
                        </td>
                        
                        {/* Bricks in Downtrend */}
                        <td style={{ color: "var(--color-text-muted)" }}>
                          {row.reversal_pattern.is_reversal_pattern 
                            ? `${row.reversal_pattern.bricks_in_downtrend} bricks`
                            : "—"
                          }
                        </td>
                        
                        {/* Date */}
                        <td style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                          {new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          {!loading && !error && displayRows.length > 0 && (
            <div style={{
              marginTop: "1.5rem",
              padding: "1rem",
              background: "rgba(30, 41, 59, 0.3)",
              borderRadius: "8px",
              border: "1px solid rgba(148, 163, 184, 0.1)"
            }}>
              <h4 style={{ margin: "0 0 0.75rem 0", fontSize: "0.9rem", color: "var(--color-text)" }}>
                Legend
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                <div>
                  <strong>Entry:</strong> First green brick close price
                </div>
                <div>
                  <strong>Stop Loss:</strong> Last red brick low
                </div>
                <div>
                  <strong>Risk:</strong> Entry - Stop Loss distance
                </div>
                <div>
                  <strong>R:R:</strong> Risk to Reward ratio (2:1 target)
                </div>
                <div>
                  <strong>Downtrend:</strong> Red bricks before reversal
                </div>
                <div>
                  <strong>Brick Sizes:</strong> Based on {metadata.method || "ATR/percent"}
                </div>
                <div>
                  <strong>Setup Weight:</strong> Reversal strength + volume surge model until true backtest edge is wired
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
